import React from 'react';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import { Paragraph } from 'elements';
import { FixedRatio, RawContent } from 'elements/util';
import { Swiper } from 'modules';
import { ArrowRight, Close, Download as DownloadIcon } from 'core/icons';
import { SiteContainer, Flex, FlexItem } from 'core/layout';
import { getDataFromACF } from 'utils/deepProps';
import isDescendant from 'utils/isDescendant';
import tulkojumi from 'utils/tulkojumi';
import { getImageUrlFromThumb } from 'utils';
import {
  StyledPopup,
  Wrapper,
  CloseButton,
  StyledGalerija,
  ImgWrapper,
  ButtonNext,
  ButtonPrev,
  StyledContent,
  DownloadLink,
} from './styled';


const GalleryQuery = gql`
  query GalleryQuery($lang: String!, $path: [String!]) {

    page(lang: $lang, path: $path) {
      title
      content
      galerija {
        thumb(size: "large") {
          url
        }
        alt
        caption
      }
      acf
    }

    teksti(lang: $lang, _id: [
      "no",
      "talak",
      "atpakal",
      "aizvert",
      "ieprieksejais_slaids",
      "nakamais_slaids",
      "sis_ir_pirmais_slaids",
      "sis_ir_pedejais_slaids",
      "iet_uz_slaidu",
      "apskatit_attelu_originalizmera"
    ]) {
      _id
      text
    }
  }
`;


class GalleryPopup extends React.PureComponent {
  state = {
    swiper: {
      index: 0,
    },
  };

  _swiperSlideChanged = (index) => {
    const { swiper } = this.state;
    if (swiper.index !== index) {
      const newSwiperState = Object.assign({}, swiper, {
        index,
      });
      this.setState(prevState => Object.assign({}, prevState, {
        swiper: newSwiperState,
      }));
    }

    if (this._aside) {
      this._aside.scrollTo(0, 0);
    }
  }

  componentDidMount() {
    document.body.classList.add('no-scroll');
    window.addEventListener('click', this._onWindowClick);
    window.addEventListener("orientationchange", this._onOrientationChange);
  }

  componentWillUnmount() {
    document.body.classList.remove('no-scroll');
    window.removeEventListener('click', this._onWindowClick);
    window.removeEventListener("orientationchange", this._onOrientationChange);
  }

  _onWindowClick = (event) => {
    const insidePopup = isDescendant(this._popupContainer, event.target);
    const isGalleryPopup = event && event.target.getAttribute('data-query-id') === 'js-gallery-popop';
    const isGalleryPagination = event && event.target.getAttribute('data-query-id') === 'js-gallery-pagination';
    if (!insidePopup && !isGalleryPopup && !isGalleryPagination) {
      const {
        onClose,
      } = this.props;
      onClose && onClose();
    }
  }

  _onOrientationChange = (event) => {
    if (this._swiper) {
      this._swiper.update();
    }
  }

  render() {
    const {
      loading,
      lang,
      teksti,
      saturs,
      galerija,
      video,
      onClose,
    } = this.props;

    if (loading) {
      return null;
    }

    const isTypeOfImageGallery = galerija && Array.isArray(galerija) && galerija.length > 0;
    const isTypeOfVideoGallery = video && video.length > 0 && video.includes('youtube');
    if (!isTypeOfImageGallery && !isTypeOfVideoGallery) {
      onClose && onClose();
    }

    const renderImage = (image, i) => {
      const url = image.thumb && Array.isArray(image.thumb) && image.thumb.length > 0 && image.thumb[0].url;
      return (
        <ImgWrapper>
          <img src={url} alt={image.alt} />
          <DownloadLink href={getImageUrlFromThumb(url)} target='_blank' title={tulkojumi(teksti, 'apskatit_attelu_originalizmera')}>
            <DownloadIcon size='large' />
          </DownloadLink>
        </ImgWrapper>
      );
    }
    const slides = galerija && galerija.map(renderImage);

    const renderYoutubeIframe = (src) => {
      return `
        <iframe
          width="100%"
          height="100%"
          src="${src}"
          frameborder="0"
          gesture="media"
          allow="encrypted-media"
        ></iframe>
      `;
    };

    const navigationProps = {
      type: 'text',
      icon: ArrowRight,
      onClick: () => { },
    };

    return (
      <StyledPopup>
        <Wrapper>
          <SiteContainer>
            <StyledGalerija innerRef={c => this._popupContainer = c}>

              <section>
                {
                  isTypeOfImageGallery && (
                    <Swiper
                      ref={c => this._swiper = c}
                      slides={slides}
                      spaceBetween
                      overlayNav
                      nextButton={
                        <ButtonNext
                          {...navigationProps}
                          data-query-id='js-gallery-pagination'
                        />
                      }
                      prevButton={
                        <ButtonPrev
                          {...navigationProps}
                          data-query-id='js-gallery-pagination'
                        />
                      }
                      beforeSlideChange={this._swiperSlideChanged}
                      teksti={teksti}
                    />
                  )
                }
                {
                  isTypeOfVideoGallery && (
                    <div
                      style={{ height: '100%' }}
                      dangerouslySetInnerHTML={{ __html: renderYoutubeIframe(video) }}
                    />
                  )
                }
              </section>

              <aside ref={c => this._aside = c}>
                <div>
                  {
                    isTypeOfImageGallery && ([
                      <span key='img-count-text'>
                        {`${this.state.swiper.index + 1} ${tulkojumi(teksti, 'no')} ${galerija.length}`}
                      </span>,
                      <Paragraph key='img-caption-text'>
                        {galerija[this.state.swiper.index].caption}
                      </Paragraph>
                    ])
                  }
                  {
                    isTypeOfVideoGallery && (
                      ([
                        <span key='img-count-text'>&nbsp;</span>,
                        <StyledContent key='img-caption-text'>
                          <RawContent content={saturs} />
                        </StyledContent>
                      ])
                    )
                  }

                  <CloseButton
                    tabIndex='0'
                    type='text'
                    accent='secondary'
                    a11yLabel={tulkojumi(teksti, 'aizvert')}
                    icon={Close}
                    onClick={() => {
                      onClose && onClose();
                    }}
                  />
                </div>
              </aside>

            </StyledGalerija>
          </SiteContainer>
        </Wrapper>
      </StyledPopup>
    );
  }
}

const GalleryPopupWithImages = graphql(GalleryQuery, {
  options: ({ lang, data }) => ({
    variables: {
      lang: lang || 'lv',
      path: data.source,
    }
  }),
  props: ({ ownProps, data: { loading, teksti, page } }) => {
    const pageData = Array.isArray(page) && page.length > 0 && page[0];
    const galerija = pageData && pageData.galerija;
    const video = getDataFromACF(page, 'video_embed_url');
    return ({
      loading,
      teksti,
      saturs: pageData.content,
      galerija,
      video,
  });
  },
})(GalleryPopup);

export default GalleryPopupWithImages;



// WEBPACK FOOTER //
// ./src/pages/Sakums/components/GalleryPopup/GalleryPopup.jsx